import { Person } from "@mui/icons-material"
import Alert from "@mui/material/Alert"
import Card from "@mui/material/Card"
import Tab from "@mui/material/Tab"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Tabs from "@mui/material/Tabs"
import Typography from "@mui/material/Typography"
import React, { useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"

import Layout from "@/components/layout"
import ReservationStatusLabel from "@/components/reservation_status_label"
import EditForm from "@/components/reservations/edit_form"
import Navigation from "@/components/reservations/navigation"
import {
  STATUS_CANCELLED,
  STATUS_CONFIRMED,
  STATUS_NO_SHOW,
  STATUS_PENDING,
  STATUS_REMOVED,
  STATUS_WALK_IN,
} from "@/constants/reservations"
import { useVenue } from "@/contexts/venue"
import { ReservationDateField, ReservationStatus } from "@/graphql/graphql"
import { useReservations } from "@/graphql/hooks"
import i18n from "@/i18n"
import { timeRange } from "@/shared/datetime"
import { usePanelLink, useReservationTabHydrate } from "@/shared/hooks"
import { TableSkeleton } from "@/shared/skeleton"
import { formatTableName } from "@/shared/tables"
import { useStore } from "@/store"

const ReservationsListPage = () => {
  const LABELS = {
    null: i18n.t("app.reservation.status.all"),
    [STATUS_CONFIRMED]: i18n.t("app.reservation.status.confirmed"),
    [STATUS_WALK_IN]: i18n.t("app.reservation.status.walk_in"),
    [STATUS_PENDING]: i18n.t("app.reservation.status.pending"),
    [STATUS_NO_SHOW]: i18n.t("app.reservation.status.no_show"),
    [STATUS_CANCELLED]: i18n.t("app.reservation.status.cancelled"),
    [STATUS_REMOVED]: i18n.t("app.reservation.status.removed"),
  }
  const STATUSES = [
    null,
    STATUS_CONFIRMED,
    STATUS_WALK_IN,
    STATUS_PENDING,
    STATUS_NO_SHOW,
    STATUS_CANCELLED,
    STATUS_REMOVED,
  ]
  const { venue } = useVenue()
  const { filter } = useParams()
  const statusFilter = filter || null
  const navigate = useNavigate()
  const linkTo = usePanelLink()
  const [previewReservationId, setPreviewReservationId] = React.useState(null)

  useReservationTabHydrate(venue)

  React.useEffect(() => {
    if (!venue.permissions?.viewReservations) {
      navigate(linkTo("settings"))
    }
  }, [venue.permissions?.viewReservations, venue.slug, navigate, linkTo])

  const { selectedTableGroupIds, currentDate } = useStore()

  const setStatusFilter = (status: string) => {
    if (!STATUSES.includes(status)) {
      return false
    }
    const path = "reservations/list" + (status ? `/${status}` : "")
    return navigate(linkTo(path))
  }

  const { reservations, loading, error } = useReservations({
    dateFilter: {
      field: ReservationDateField.ReservationDate,
      from: currentDate,
    },
    status: statusFilter ? ([statusFilter] as ReservationStatus[]) : undefined,
    includeRemoved: statusFilter === "removed",
    tableGroups: selectedTableGroupIds as string[],
  })

  useEffect(() => {
    if (statusFilter && !STATUSES.includes(statusFilter)) {
      setStatusFilter(null)
    }
  }, [statusFilter])

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setStatusFilter(STATUSES[newValue])
  }
  const currentTab = STATUSES.indexOf(statusFilter)

  return (
    <Layout title="Reservations" flex data-testid="panel-reservations-list-page">
      {error ? (
        <Alert variant="filled" severity="error">
          {JSON.stringify(error)}
        </Alert>
      ) : null}

      <Navigation />

      <Card variant="outlined" className="p-4 flex-1">
        <div className="border-b border-gray-200">
          <Tabs value={currentTab} onChange={handleChange}>
            {STATUSES.map((status, index) => (
              <Tab
                key={index}
                label={LABELS[status]}
                data-testid={`panel-reservations-list-tab-${status || "all"}`}
                className={index === STATUSES.length - 1 ? "self-end ml-auto" : ""}
              />
            ))}
          </Tabs>
        </div>

        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Hours</TableCell>
                <TableCell>Table</TableCell>
                <TableCell>Seats</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            {loading ? (
              <TableSkeleton rows={3} columns={7} delay={100} fadingAway className="h-40" />
            ) : (
              <TableBody>
                {reservations?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center h-40">
                      {i18n.t("app.reservations_list.empty")}
                    </TableCell>
                  </TableRow>
                )}
                {reservations?.map((reservation) => (
                  <TableRow
                    key={reservation.id}
                    onClick={() => setPreviewReservationId(reservation.id)}
                    data-testid={`panel-reservations-list-row-${reservation.id}`}
                  >
                    <TableCell component="th" scope="row">
                      #{reservation.id}
                    </TableCell>
                    <TableCell>
                      <Typography>{reservation.customerContact?.name || "-"}</Typography>
                      <Typography variant="subtitle2">{reservation.customerContact?.email || "-"}</Typography>
                    </TableCell>
                    <TableCell>{reservation.customerContact?.phoneFormatted || "-"}</TableCell>
                    <TableCell>{timeRange(reservation.startTime, reservation.endTime)}</TableCell>
                    <TableCell>{formatTableName(reservation.table)}</TableCell>
                    <TableCell>
                      {reservation.peopleAmount}
                      <Person className="align-bottom ml-0.5" />
                    </TableCell>
                    <TableCell>
                      <ReservationStatusLabel status={reservation.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Card>

      <EditForm venue={venue} reservationId={previewReservationId} onClose={() => setPreviewReservationId(null)} />
    </Layout>
  )
}

export default ReservationsListPage
